import _ from "lodash";
import API from "@/api";
import staticUrl from "@/static";

// NOTE: This object may be split out into multiple smaller objects as it grows.
class TFMService {
	async getTotalStations() {
		const rawDevices = await API.fetchJson(
			API.ENDPOINTS.GET_TOTAL_STATIONS_DEVICES
		);
		const rawData = await API.fetchJson(API.ENDPOINTS.GET_TOTAL_STATIONS);
		const totalStations = _.chain(rawData)
			.map(ts => ({
				totalStationId: ts.totalStationId,
				description: ts.totalStationName,
				locationDesc: ts.locationDescription,
				deviceCount: ts.deviceCount,
				numFunctional: ts.deviceFunctionalCount,
				isAllFunctional: ts.deviceCount == ts.deviceFunctionalCount,
				latitude: ts.latitude,
				longitude: ts.longitude,
				devices: {},
				calculatedTSStatus: ts.calculatedTSStatus,
				totalStationSCRStatus: ts.totalStationSCRStatus,
				totalStationsStatus: ts.totalStationStatus,
				totalStationSCRId: ts.totalStationSCRId,
				totalStationOperatorId: ts.operatorId,
				totalStationOwnerId: ts.ownerId
			}))
			.keyBy("totalStationId")
			.value();
		_.each(rawDevices, d => {
			totalStations[d.totalStationId].devices[d.deviceId] = Object.assign(
				{
					/** stored proc returns 0 for proposed, 1 for deployed and 2 for in maintenance devices */
					$deviceTypeId: d.deviceTypeId,
					isSCRDraft: d.deviceStatus == 0,
					isDeployed: d.deviceStatus == 1,
					isInMaintenance: d.deviceStatus == 2,
					projectId: d.ProjectId
				},
				d
			);
		});
		_.each(totalStations, function(ts) {
			let deviceCount = _.size(ts.devices);
			let numFunctional = _.size(_.reject(ts.devices, { isDeployed: false }));
			let numDysfunctional = _.size(_.reject(ts.devices, { isDeployed: true }));
			let totalStationSCRId = ts.totalStationSCRId;
			Object.assign(ts, {
				deviceCount: deviceCount,
				numFunctional: numFunctional,
				numDysfunctional: numDysfunctional,
				isAllFunctional: deviceCount === numFunctional,
				totalStationSCRId: totalStationSCRId
			});
		});
		return totalStations;
	}

	async getIpAddressByDeviceId(deviceId) {
		const data = await API.fetchJson(
			API.ENDPOINTS.GET_IP_ADDRESS_BY_DEVICE_ID + `?deviceId=${deviceId}`
		);
		if (!data || data.length < 1) return null;
		return data[0];
	}

	async fetchClientsByUser() {
		const data = await API.fetchJson(API.ENDPOINTS.GET_CLIENT_BY_USER_LIST);
		if (data.length < 1) return null;
		return data;
	}

	async getDeviceTypes() {
		const rawDeviceTypes = await API.fetchJson(API.ENDPOINTS.GET_DEVICE_TYPES);
		const m = _.map(rawDeviceTypes, dt => {
			return Object.assign(dt, { iconURL: staticUrl(dt.iconURL) });
		});
		return _.keyBy(m, "deviceTypeId");
	}

	async getRSUManageInfo(deviceId) {
		const data = await API.fetchJson(
			API.ENDPOINTS.GET_RSU_MANAGE_INFO + `?deviceId=${deviceId}`
		);
		return data;
	}

	async getTotalStationManageInfo(totalStationId) {
		const data = await API.fetchJson(
			API.ENDPOINTS.GET_TOTALSTATION_MANAGE_INFO +
				`?totalStationId=${totalStationId}`
		);
		if (data.length < 1) return null;
		return data[0];
	}

	async getESSManageInfo(deviceId) {
		const data = await API.fetchJson(
			API.ENDPOINTS.GET_ESS_MANAGE_INFO + `?deviceId=${deviceId}`
		);
		if (data.length < 1) return null;
		return data[0];
	}

	async getTSCManageInfo(deviceId) {
		const data = await API.fetchJson(
			API.ENDPOINTS.GET_TSC_MANAGE_INFO + `?deviceId=${deviceId}`
		);
		if (data.length < 1) return null;
		return data[0];
	}

	async doCommandProxyAction(deviceId, actionId, jobKey, srmId, srmType) {
		// TODO: add options?
		const data = await API.fetchJson(
			API.ENDPOINTS.COMMAND_PROXY_SCHEDULE +
				`?deviceId=${deviceId}&actionId=${actionId}&jobKey=${jobKey}&info=${srmId}&srmType=${srmType}`,
			null,
			error => {
				throw error;
			}
		);
		// console.debug("doCommandProxyAction data", data);
		if (data.length < 1) return null;
		return data[0];
	}

	async doCommandProxyActionTest(
		deviceId,
		actionId,
		jobKey,
		timId,
		labId,
		testIP,
		extInfo
	) {
		// TODO: add options?
		const data = await API.fetchJson(
			API.ENDPOINTS.COMMAND_PROXY_SCHEDULE_TEST +
				`?deviceId=${deviceId}&actionId=${actionId}&jobKey=${jobKey}&info=${timId}&labId=${labId}&testIP=${testIP}&extInfo=${extInfo}`,
			{},
			e => {
				throw e;
			}
		);
		// console.debug("doCommandProxyAction data", data);
		if (data.length < 1) return null;
		return data[0];
	}

	async doCommandProxyLiteralAction(
		deviceId,
		actionId,
		jobKey,
		msgInfo,
		labId,
		testIP,
		extInfo
	) {
		// TODO: add options?
		const data = await API.fetchJson(
			API.ENDPOINTS.COMMAND_PROXY_LITERAL_ACTION +
				`?deviceId=${deviceId}&actionId=${actionId}&jobKey=${jobKey}&msgInfo=${msgInfo}&labId=${labId}&testIP=${testIP}&extInfo=${extInfo}`,
			{},
			e => {
				throw e;
			}
		);
		if (data.length < 1) return null;
		return data[0];
	}

	// This is just for demo purposes.
	async doRsuConfig1218LiteralAction(deviceId, actionId, jobKey, msgInfo) {
		// TODO: add options?
		const data = await API.fetchJson(
			API.ENDPOINTS.RSU_CONFIG_1218_LITERAL_ACTION +
				`?deviceId=${deviceId}&actionId=${actionId}&jobKey=${jobKey}&msgInfo=${msgInfo}`,
			{},
			e => {
				throw e;
			}
		);

		if (data.length < 1) {
			return null;
		}

		return data[0];
	}

	async getDashboardStatusTs(tsStatusId) {
		let route = API.ENDPOINTS.GET_STATUS_TS;
		if (!_.isNil(tsStatusId)) {
			route += `/${tsStatusId}`;
		}
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}

	async getDashboardStatusRsu(deviceStatusId) {
		let route = API.ENDPOINTS.GET_STATUS_RSU;
		if (!_.isNil(deviceStatusId)) {
			route += `/${deviceStatusId}`;
		}
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}

	async getDashboardStatusTsc(deviceStatusId) {
		let route = API.ENDPOINTS.GET_STATUS_TSC;
		if (!_.isNil(deviceStatusId)) {
			route += `/${deviceStatusId}`;
		}
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}

	async getDashboardStatusEss(deviceStatusId) {
		let route = API.ENDPOINTS.GET_STATUS_ESS;
		if (!_.isNil(deviceStatusId)) {
			route += `/${deviceStatusId}`;
		}
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}

	async getUnverifiedHeadings(agency) {
		let route = API.ENDPOINTS.UNVERIFIED_HEADINGS;
		if (!_.isNil(agency)) {
			route += `/?agency=${agency}`;
		}
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}

	async setHeadingSlice(
		agency,
		dynamicMessageSignId,
		headingSlice,
		headingVerified
	) {
		let route = API.ENDPOINTS.UNVERIFIED_HEADINGS;
		const data = {
			agency,
			dynamicMessageSignId,
			headingSlice,
			headingVerified
		};
		const response = await API.postJson(route, data, {}, e => {
			throw e;
		});
		return response;
	}

	async getFloodData() {
		let route = API.ENDPOINTS.GET_FLOOD_LIKELIHOOD;
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}

	async getWeatherStationsByArea(
		topLeftLat,
		topLeftLong,
		bottomRightLat,
		bottomRightLong
	) {
		let route = API.ENDPOINTS.GET_WEATHERSTATIONS_BY_AREA;
		route += `/?topLeftLat=${topLeftLat}&topLeftLong=${topLeftLong}&bottomRightLat=${bottomRightLat}&bottomRightLong=${bottomRightLong}`;
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}

	async getStreamGagesByByArea(
		topLeftLat,
		topLeftLong,
		bottomRightLat,
		bottomRightLong
	) {
		let route = API.ENDPOINTS.GET_STREAMGAGES_BY_AREA;
		route += `/?topLeftLat=${topLeftLat}&topLeftLong=${topLeftLong}&bottomRightLat=${bottomRightLat}&bottomRightLong=${bottomRightLong}`;
		const data = await API.fetchJson(route, {}, e => {
			throw e;
		});
		return data;
	}
}

export default new TFMService();
